import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
} from 'react';
import {
  ListingInsightsResponse,
  ListingActiveAdsResponse,
  ActiveAd,
} from '../system/promote';
import { useTenant } from './TenantContext';
import { useFeatureFlags } from './FeatureFlagsContext';
import { FeatureFlags } from '../flags';

export interface PromoteServiceContextType {
  getListingInsights: (
    listingId: string,
  ) => Promise<ListingInsightsResponse | null>;
  getActiveAds: (listingId: string) => Promise<ActiveAd[]>;
}

const PromoteServiceContext = createContext<
  PromoteServiceContextType | undefined
>(undefined);

export const PromoteServiceProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { hasFeature } = useFeatureFlags();
  const { getConfigValue } = useTenant();

  const promoteGet = useCallback(async <T,>(path: string): Promise<T> => {
    const baseUrl = getConfigValue<string>('PROMOTE_BASE_URL');
    const response = await fetch(`${baseUrl}${path}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(
        `Promote API error: ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  }, []);

  const getListingInsights = useCallback(
    async (listingId: string): Promise<ListingInsightsResponse | null> => {
      if (!hasFeature(FeatureFlags.NGW_OVERVIEW_PROMOTE_STATS)) {
        return null;
      }
      try {
        return await promoteGet<ListingInsightsResponse>(
          `/listings/${encodeURIComponent(listingId)}/insights`,
        );
      } catch (error) {
        console.error('Failed to fetch listing insights:', error);
        return null;
      }
    },
    [promoteGet, hasFeature],
  );

  const getActiveAds = useCallback(
    async (listingId: string): Promise<ActiveAd[]> => {
      try {
        const response = await promoteGet<ListingActiveAdsResponse>(
          `/listings/${encodeURIComponent(listingId)}/ads/active`,
        );
        return response.active_ads;
      } catch (error) {
        console.error('Failed to fetch active ads:', error);
        return [];
      }
    },
    [promoteGet],
  );

  return (
    <PromoteServiceContext.Provider
      value={{ getListingInsights, getActiveAds }}
    >
      {children}
    </PromoteServiceContext.Provider>
  );
};

// -- Hook -------------------------------------------------------------------

export const usePromoteService = () => {
  const context = useContext(PromoteServiceContext);
  if (context === undefined) {
    throw new Error(
      'usePromoteService must be used within a PromoteServiceProvider',
    );
  }
  return context;
};
